// ============================================
// Jupiter Shaders
// Fragment and vertex shaders for Jupiter rendering
// ============================================

import { earthShaderFunctions } from './common';

/**
 * Jupiter surface vertex shader
 */
export const jupiterVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        vNormal = normalize(normalMatrix * normal);
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

/**
 * Jupiter surface fragment shader - banded atmosphere with Great Red Spot
 */
export const jupiterFragmentShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vPosition;
    uniform float time;
    uniform vec3 sunDirection;

    ${earthShaderFunctions}

    void main() {
        vec2 uv = vUv;

        // Fixed view direction
        vec3 viewDir = vec3(0.0, 0.0, 1.0);
        vec3 normal = normalize(vNormal);

        // Fresnel coefficient
        float fresnel = 1.0 - max(0.0, dot(normal, viewDir));
        float fresnelPow = pow(fresnel, 1.5);

        // Sun light calculation
        float sunLight = max(0.0, dot(normal, sunDirection));
        float ambient = 0.3;
        float diffuse = sunLight * 0.7;
        float lighting = ambient + diffuse;

        // ===== Jupiter's banded atmosphere =====
        // Base colors for Jupiter's bands
        vec3 lightBand = vec3(0.90, 0.85, 0.75);    // Light zones (yellowish-white)
        vec3 darkBand = vec3(0.75, 0.55, 0.40);     // Dark belts (brownish-orange)
        vec3 equatorBand = vec3(0.85, 0.70, 0.55);  // Equatorial zone
        vec3 polarBand = vec3(0.70, 0.65, 0.60);    // Polar regions

        // Latitude-based banding
        float latitude = uv.y;

        // Create horizontal bands with noise
        float bandNoise = noise(vec2(uv.x * 30.0 + time * 0.02, latitude * 5.0)) * 0.08;
        float bandPattern = sin(latitude * 25.0 + bandNoise * 5.0) * 0.5 + 0.5;

        // Turbulence in bands
        float turbulence = fbm(vec2(uv.x * 15.0 + time * 0.03, latitude * 20.0), 4) * 0.15;
        bandPattern += turbulence;

        // Mix band colors based on pattern
        vec3 bandColor = mix(darkBand, lightBand, bandPattern);

        // Equatorial region (brighter)
        float equatorFactor = 1.0 - smoothstep(0.4, 0.6, abs(latitude - 0.5) * 2.0);
        bandColor = mix(bandColor, equatorBand, equatorFactor * 0.4);

        // Polar darkening
        float polarFactor = smoothstep(0.3, 0.0, abs(latitude - 0.5) * 2.0 - 0.7);
        bandColor = mix(bandColor, polarBand, polarFactor);

        // ===== Great Red Spot =====
        vec2 grsCenter = vec2(0.65 + time * 0.001, 0.35);  // Below equator
        vec2 grsSize = vec2(0.12, 0.06);  // Elliptical shape

        // Distance to GRS center (elliptical)
        vec2 grsUv = (uv - grsCenter) / grsSize;
        float grsDist = length(grsUv);

        // GRS colors
        vec3 grsOuter = vec3(0.85, 0.45, 0.35);  // Reddish outer
        vec3 grsInner = vec3(0.95, 0.55, 0.45);  // Lighter inner

        // GRS shape with swirl
        float grsAngle = atan(grsUv.y, grsUv.x);
        float grsSwirl = sin(grsAngle * 3.0 - grsDist * 10.0 + time * 0.1) * 0.15;
        float grsFactor = smoothstep(1.2 + grsSwirl, 0.5, grsDist);

        // Apply GRS
        vec3 grsColor = mix(grsOuter, grsInner, smoothstep(0.8, 0.2, grsDist));
        bandColor = mix(bandColor, grsColor, grsFactor * 0.9);

        // ===== Small storms (white ovals) =====
        vec2 storm1 = vec2(0.25, 0.55);
        vec2 storm2 = vec2(0.45, 0.70);
        float storm1Dist = length((uv - storm1) / vec2(0.03, 0.015));
        float storm2Dist = length((uv - storm2) / vec2(0.025, 0.012));
        float stormFactor = smoothstep(1.0, 0.3, storm1Dist) + smoothstep(1.0, 0.3, storm2Dist);
        bandColor = mix(bandColor, vec3(0.95, 0.92, 0.88), stormFactor * 0.6);

        // Apply lighting
        vec3 surfaceColor = bandColor * lighting;

        // Atmospheric glow at edges
        vec3 atmosphereColor = vec3(0.85, 0.75, 0.60);
        float rimLight = pow(fresnel, 2.0) * 0.4;
        surfaceColor = mix(surfaceColor, atmosphereColor, rimLight);

        // Night side darkening
        float nightSide = 1.0 - smoothstep(0.0, 0.15, sunLight);
        surfaceColor *= mix(1.0, 0.3, nightSide);

        gl_FragColor = vec4(surfaceColor, 1.0);
    }
`;
